/**
 * One control for a date range: a button showing the range that opens a
 * two-month calendar (one month on phones). The first click picks the start,
 * the second the end, and a third starts a new range. Values are "YYYY-MM-DD"
 * strings ("" = open-ended), so they compare directly with ISO dates in rows.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
// Exported through the package's "exports" map, which Vite resolves and the lint resolver does not.
// eslint-disable-next-line import/no-unresolved
import "react-day-picker/style.css";
import { Icon } from "./Icon.jsx";
import { controlClass } from "./Field.jsx";
import { rangeText, toDate, toIso } from "./dateRange.js";
import { popoverClass, useDismiss } from "./useDismiss.js";

/** True while the viewport is at least `px` wide. */
function useWide(px = 640) {
  const query = `(min-width: ${px}px)`;
  const [wide, setWide] = useState(() => typeof window === "undefined" || !window.matchMedia || window.matchMedia(query).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return wide;
}

/**
 * The calendar alone (months side by side), for popovers that keep their own
 * buttons, like the top bar's Apply / Cancel. Same `value`/`min`/`max` as below.
 */
export function RangeCalendar({ value, onChange, min, max }) {
  const wide = useWide();
  const from = toDate(value.from);
  const lo = toDate(min);
  const hi = toDate(max);
  // Open on the chosen start, else on the last selectable month (and the one before it).
  const anchor = from ?? (hi ? new Date(hi.getFullYear(), hi.getMonth() - (wide ? 1 : 0), 1) : undefined);
  return (
    <div className="ca-range">
      <DayPicker
        mode="range"
        numberOfMonths={wide ? 2 : 1}
        resetOnSelect
        selected={{ from, to: toDate(value.to) }}
        onSelect={(r) => onChange({ from: toIso(r?.from), to: toIso(r?.to) })}
        defaultMonth={anchor}
        startMonth={lo}
        endMonth={hi}
        disabled={[lo && { before: lo }, hi && { after: hi }].filter(Boolean)}
        weekStartsOn={1}
      />
    </div>
  );
}

/** `value`: { from, to }; `min`/`max` bound the selectable days ("YYYY-MM-DD"). */
export function DateRangePicker({ label, value, onChange, min, max, placeholder = "Any date", className = "" }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useDismiss(wrap, open, useCallback(() => setOpen(false), []));
  const text = value.from || value.to ? rangeText(value) : placeholder;

  return (
    <div ref={wrap} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${text}`}
        onClick={() => setOpen((o) => !o)}
        className={`${controlClass} tabular flex h-9 items-center gap-2 px-3 text-left text-[12px] ${value.from || value.to ? "" : "text-ink-3"}`}
      >
        <Icon name="calendar" className="size-4 shrink-0 text-ink-3" />
        <span className="truncate">{text}</span>
      </button>
      {open && (
        // w-max: as wide as the calendar, not the trigger, so the months sit side by side.
        <div role="dialog" aria-label={`${label} range`} className={`${popoverClass} absolute left-0 top-full mt-1.5 w-max p-3`}>
          <RangeCalendar value={value} onChange={onChange} min={min} max={max} />
          <div className="mt-2 flex items-center justify-between gap-3 border-t border-line pt-2.5">
            <span className="text-[11.5px] text-ink-3">{!value.from ? "Pick a start date" : !value.to ? "Now pick an end date" : text}</span>
            <span className="flex gap-3">
              {(value.from || value.to) && (
                <button type="button" className="text-[12px] text-ink-2 underline underline-offset-2 hover:text-ink" onClick={() => onChange({ from: "", to: "" })}>
                  Reset
                </button>
              )}
              <button type="button" className="text-[12px] font-semibold text-accent-ink" onClick={() => setOpen(false)}>
                Done
              </button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
